/**
 * Registro de un pago en caja (F3-H, `ManagePayments`): alumno, método, moneda
 * (USD/VES con tasa aplicada), referencia, foto de comprobante y alocación sobre
 * las facturas abiertas del alumno. Entra como `pending_verification` — el admin
 * lo verifica después (separación de deberes). Debajo, la lista de pagos con
 * filtro por estado.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Camera, Receipt } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { uploadStorageObject } from "@/lib/api";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { Money } from "./money";
import {
  type AllocationInput,
  useInvoices,
  useMethods,
  usePayments,
  useRates,
  useRecordPayment,
} from "../api";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";
const MINOR = 100;

const paymentBadgeClass = (status: string): string => {
  if (status === "verified") {
    return "bg-emerald-100 text-emerald-700";
  }
  if (status === "rejected") {
    return "bg-red-100 text-red-700";
  }
  return "bg-amber-100 text-amber-700";
};

export const RecordPayment: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const methods = useMethods(activeOrgId);
  const rates = useRates(activeOrgId);
  const record = useRecordPayment();

  const [studentId, setStudentId] = createSignal("");
  const [methodKey, setMethodKey] = createSignal("");
  const [currency, setCurrency] = createSignal<"USD" | "VES">("USD");
  const [amount, setAmount] = createSignal("");
  const [reference, setReference] = createSignal("");
  const [receiptKey, setReceiptKey] = createSignal<string | undefined>();
  const [uploading, setUploading] = createSignal(false);
  const [allocs, setAllocs] = createSignal<Record<string, number>>({});
  const [statusFilter, setStatusFilter] = createSignal<string | undefined>();
  // Id de operación por INTENTO de formulario: estable entre reintentos del
  // mismo envío (el servidor deduplica double-submit por este id) y regenerado
  // tras cada registro exitoso para permitir el siguiente pago.
  const [opId, setOpId] = createSignal(crypto.randomUUID());

  const activeStudent = () => studentId().trim() || undefined;
  const openInvoices = useInvoices(
    activeOrgId,
    activeStudent,
    () => undefined,
    () => false,
  );
  const payments = usePayments(activeOrgId, statusFilter);

  const selectedMethod = () => methods.data?.find((m) => m.key === methodKey());
  const latestRate = () => rates.data?.[0];

  /** Unidades menores de lo TECLEADO, en la moneda elegida. */
  const typedMinor = createMemo(() => {
    const parsed = Number(amount().replace(",", "."));
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * MINOR) : 0;
  });

  const usdMinor = createMemo(() => {
    const minor = typedMinor();
    if (minor === 0 || currency() === "USD") {
      return minor;
    }
    const rate = latestRate()?.vesPerUsd;
    return rate && rate > 0 ? Math.round(minor / rate) : 0;
  });

  const enabledMethods = () => methods.data?.filter((m) => m.enabled && m.kind === "manual") ?? [];

  const onUpload = async (e: Event & { currentTarget: HTMLInputElement }) => {
    const input = e.currentTarget;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      const result = await uploadStorageObject(file);
      setReceiptKey(result.key || result.url);
      showToast(t(msg`Receipt uploaded`), "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), "error");
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    const method = selectedMethod();
    const student = activeStudent();
    if (!student || !method) {
      showToast(t(msg`Choose a student and method`), "error");
      return;
    }
    const parsed = Number(amount().replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      showToast(t(msg`Enter a valid amount`), "error");
      return;
    }
    let exchangeRateId: string | undefined = undefined;
    if (currency() === "VES") {
      const rate = latestRate();
      if (!rate) {
        showToast(t(msg`Set today's rate before recording a VES payment`), "error");
        return;
      }
      exchangeRateId = rate.id;
    }
    const allocations: AllocationInput[] = Object.entries(allocs())
      .filter(([, v]) => v > 0)
      .map(([invoiceId, v]) => ({ invoiceId, amountUsdMinor: Math.round(v * MINOR) }));

    record.mutate(
      {
        org: activeOrgId(),
        studentUserId: student,
        methodKey: method.key,
        currency: currency(),
        amountMinor: Math.round(parsed * MINOR),
        exchangeRateId,
        referenceNumber: reference().trim() || undefined,
        receiptAssetKey: receiptKey(),
        allocations,
        clientOperationId: opId(),
      },
      {
        onSuccess: () => {
          showToast(t(msg`Payment recorded (pending verification)`), "success");
          setAmount("");
          setReference("");
          setReceiptKey(undefined);
          setAllocs({});
          setOpId(crypto.randomUUID());
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  return (
    <div class="space-y-6">
      <div class="rounded-xl border border-border p-4">
        <h2 class="mb-3 flex items-center gap-2 font-semibold">
          <Receipt class="h-5 w-5 text-primary" />
          {t(msg`Record payment`)}
        </h2>

        <div class="grid gap-3 md:grid-cols-2">
          <label class="space-y-1">
            <span class="text-xs text-muted-foreground">{t(msg`Student (user id)`)}</span>
            <input
              class={field}
              value={studentId()}
              onInput={(e) => setStudentId(e.currentTarget.value)}
              placeholder={t(msg`user id`)}
              data-testid="payment-student"
            />
          </label>

          <label class="space-y-1">
            <span class="text-xs text-muted-foreground">{t(msg`Method`)}</span>
            <Select
              aria-label={t(msg`Method`)}
              value={methodKey()}
              options={enabledMethods().map((m) => ({ value: m.key, label: m.name }))}
              onChange={(v) => {
                setMethodKey(v);
                const m = methods.data?.find((x) => x.key === v);
                const cur = m?.currencies[0] ?? "USD";
                setCurrency(cur);
              }}
              placeholder={t(msg`Select method`)}
            />
          </label>

          <div class="space-y-1">
            <span class="text-xs text-muted-foreground">{t(msg`Currency`)}</span>
            <div class="flex gap-2">
              <For each={selectedMethod()?.currencies ?? (["USD"] as const)}>
                {(c) => (
                  <button
                    type="button"
                    class={`rounded-lg border-2 px-3 py-1.5 text-sm ${
                      currency() === c ? "border-primary bg-primary/10" : "border-border"
                    }`}
                    onClick={() => setCurrency(c)}
                    data-testid={`currency-${c}`}
                  >
                    {c}
                  </button>
                )}
              </For>
            </div>
          </div>

          <label class="space-y-1">
            <span class="text-xs text-muted-foreground">{t(msg`Amount`)}</span>
            <input
              class={field}
              inputmode="decimal"
              value={amount()}
              onInput={(e) => setAmount(e.currentTarget.value)}
              placeholder="0.00"
              data-testid="payment-amount"
            />
          </label>

          <Show when={selectedMethod()?.requiresReference}>
            <label class="space-y-1">
              <span class="text-xs text-muted-foreground">{t(msg`Reference number`)}</span>
              <input
                class={field}
                value={reference()}
                onInput={(e) => setReference(e.currentTarget.value)}
                data-testid="payment-reference"
              />
            </label>
          </Show>

          <label class="space-y-1">
            <span class="text-xs text-muted-foreground">{t(msg`Receipt photo`)}</span>
            <div class="flex items-center gap-2">
              <label class="inline-flex cursor-pointer items-center gap-1 rounded-lg border-2 border-border px-3 py-2 text-sm">
                <Camera class="h-4 w-4" />
                {uploading() ? t(msg`Uploading…`) : t(msg`Upload`)}
                <input type="file" accept="image/*" class="hidden" onChange={onUpload} />
              </label>
              <Show when={receiptKey()}>
                <span class="text-xs text-emerald-600">✓</span>
              </Show>
            </div>
          </label>
        </div>

        <Show when={usdMinor() > 0}>
          <p class="mt-3 text-sm">
            {t(msg`Equivalent`)}:{" "}
            {/* El bolívar de esta línea es el que el operador acaba de TECLEAR,
                no una reconversión: el pago aún no existe y el servidor todavía
                no tiene nada que convertir. Para un cobro en USD la equivalencia
                es el propio importe y no hay bolívar que enseñar. */}
            <Money
              usdMinor={usdMinor()}
              vesMinor={currency() === "VES" ? typedMinor() : null}
              vesPerUsd={latestRate()?.vesPerUsd}
            />
          </p>
        </Show>

        {/* Alocación sobre facturas abiertas del alumno */}
        <Show when={activeStudent() && (openInvoices.data?.length ?? 0) > 0}>
          <div class="mt-4">
            <h3 class="mb-2 text-sm font-semibold">{t(msg`Allocate to invoices`)}</h3>
            <ul class="space-y-2" data-testid="allocations">
              <For
                each={openInvoices.data?.filter((i) => i.status !== "paid" && i.status !== "void")}
              >
                {(inv) => (
                  <li class="flex items-center justify-between gap-2 text-sm">
                    <span>
                      {inv.concept} {inv.seqLabel ? `· ${inv.seqLabel}` : ""}
                      <span class="ml-2 text-muted-foreground">
                        ({t(msg`due`)} <Money usdMinor={inv.remainingUsdMinor} />)
                      </span>
                    </span>
                    <input
                      class={`${field} max-w-[8rem]`}
                      inputmode="decimal"
                      placeholder="0.00"
                      onInput={(e) => {
                        const v = Number(e.currentTarget.value.replace(",", "."));
                        setAllocs((prev) => ({ ...prev, [inv.id]: Number.isFinite(v) ? v : 0 }));
                      }}
                    />
                  </li>
                )}
              </For>
            </ul>
          </div>
        </Show>

        <div class="mt-4">
          <Button onClick={submit} disabled={record.isPending} data-testid="payment-submit">
            {t(msg`Record payment`)}
          </Button>
        </div>
      </div>

      {/* Lista de pagos */}
      <div class="rounded-xl border border-border p-4">
        <div class="mb-2 flex items-center justify-between">
          <h3 class="font-semibold">{t(msg`Payments`)}</h3>
          <Select
            aria-label={t(msg`Status`)}
            value={statusFilter() ?? ""}
            options={[
              { value: "", label: t(msg`All`) },
              { value: "pending_verification", label: t(msg`Pending`) },
              { value: "verified", label: t(msg`Verified`) },
              { value: "rejected", label: t(msg`Rejected`) },
            ]}
            onChange={(v) => setStatusFilter(v || undefined)}
            triggerClass="w-40"
          />
        </div>
        <Show when={!payments.isLoading} fallback={<Skeleton class="h-24 w-full" />}>
          <ul class="divide-y divide-border" data-testid="payments-list">
            <For each={payments.data}>
              {(p) => (
                <li class="flex items-center justify-between py-2 text-sm">
                  <span class="truncate">{p.studentUserId}</span>
                  <Money usdMinor={p.amountUsdMinor} />
                  <span class="text-xs text-muted-foreground">{p.methodKey}</span>
                  <span class={`rounded-full px-2 py-0.5 text-xs ${paymentBadgeClass(p.status)}`}>
                    {p.status}
                  </span>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </div>
  );
};
